import { type ReactNode, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Home, Camera, Settings, LogOut, ChevronRight } from 'lucide-react';
import { signOut, useAuth } from '../lib/auth';

export default function Layout({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [logoError, setLogoError] = useState(false);

  if (!user) return <>{children}</>;

  const items = [
    { to: '/', label: 'Översikt', icon: Home },
    { to: '/foto', label: 'Foto', icon: Camera },
    ...(user.role === 'admin' ? [{ to: '/admin', label: 'Admin', icon: Settings }] : []),
  ];

  function handleLogout() {
    void (async () => {
      await signOut();
      await navigate('/login', { replace: true });
    })();
  }

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-surface-cream overflow-x-hidden">
      <aside className="hidden md:flex flex-col w-64 bg-primary text-white shadow-xl">
        <div className="p-6 border-b border-white/10 text-center">
          {logoError ? (
            <div className="w-24 h-24 bg-white rounded-full mx-auto mb-3 flex items-center justify-center">
              <span className="text-primary font-bold text-3xl font-serif">W:6</span>
            </div>
          ) : (
            <div className="w-24 h-24 bg-white rounded-full mx-auto mb-3 flex items-center justify-center overflow-hidden">
              <img
                src="/w6-logo.png"
                alt="W:6 Logo"
                className="w-full h-full object-contain p-1"
                onError={() => { setLogoError(true); }}
              />
            </div>
          )}
          <h1 className="text-xl font-bold font-serif">Stamlogen 2026</h1>
          <p className="text-xs text-white/60 tracking-widest mt-1">EST. 1851</p>
        </div>

        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                `w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                  isActive ? 'bg-primary-dark text-white shadow-md' : 'hover:bg-white/10 text-white/80'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <item.icon size={20} />
                  <span className="font-medium">{item.label}</span>
                  {isActive && <ChevronRight size={16} className="ml-auto" />}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-white/10">
          <button
            type="button"
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-white/40 hover:text-white/80 hover:bg-white/5 transition-all"
          >
            <LogOut size={20} />
            <span className="font-medium">Logga ut</span>
          </button>
          <div className="mt-4 text-center text-[10px] text-white/20">
            © 2026 Stamlogen W6
          </div>
        </div>
      </aside>

      <header className="md:hidden bg-primary text-white p-4 sticky top-0 z-50 flex items-center justify-between shadow-lg">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
            <span className="text-primary font-bold text-sm font-serif">W:6</span>
          </div>
          <span className="font-serif font-bold tracking-tight text-lg">STAMLOGEN 2026</span>
        </div>
        <button
          type="button"
          onClick={handleLogout}
          className="text-white/60 text-xs font-bold flex items-center gap-1"
        >
          <LogOut size={14} /> LOGGA UT
        </button>
      </header>

      <main className="flex-1 min-w-0 p-4 md:p-8 overflow-y-auto pb-24 md:pb-8">{children}</main>

      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 flex justify-around py-2 z-50">
        {items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/'}
            className={({ isActive }) =>
              `flex flex-col items-center gap-1 p-2 min-w-[58px] rounded-lg ${
                isActive ? 'text-primary-dark' : 'text-gray-400'
              }`
            }
          >
            <item.icon size={18} />
            <span className="text-[10px] font-bold uppercase">{item.label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
